import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import radioClasses from './Radio.module.scss';

export function RadioLabel({ children }) {
  return <span className={radioClasses.label}>{children}</span>;
}

export function RadioOption({ value, label, checked }) {
  return { value, label, checked };
}

export function Radio({ id, name, onChange, value, label, checked }) {
  const radioRef = useRef();

  const handleChange = (e) => {
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <div className={radioClasses.radioOption}>
      <input
        ref={radioRef}
        type="radio"
        id={id}
        name={name}
        className={radioClasses.radio}
        onChange={handleChange}
        value={value}
        checked={checked}
      />
      <label htmlFor={id} className={radioClasses.container}>
        <RadioLabel>{label}</RadioLabel>
      </label>
    </div>
  );
}

export default function RadioContainer({
  name,
  options,
  className,
  onChange,
  initialValue,
  value,
  customStyle,
  prefix,
  suffix,
}) {
  const [internalValue, setInternalValue] = useState(initialValue ?? null);

  // Use controlled value if provided, otherwise use internal state
  const selectedValue = value !== undefined ? value : internalValue;

  const handleChange = (e) => {
    const { value: newValue } = e.target;

    // Only update internal state when in uncontrolled mode
    if (value === undefined) {
      setInternalValue(newValue);
    }

    if (onChange) {
      onChange(newValue);
    }
  };

  const cn = cx(radioClasses.field, className);

  return (
    <div className={cn} style={customStyle}>
      {prefix}
      {options.map((option, index) => {
        const opt =
          typeof option === 'object' && React.isValidElement(option)
            ? {
                value: option.props.value,
                label: option.props.label,
                checked: option.props.checked,
              }
            : option;

        return (
          <Radio
            key={`${name}-${index}`}
            id={`${name}-${index}`}
            name={name}
            onChange={handleChange}
            checked={selectedValue === opt.value}
            value={opt.value}
            label={opt.label}
          />
        );
      })}
      {suffix}
    </div>
  );
}

RadioLabel.propTypes = {
  children: PropTypes.node,
};

RadioOption.propTypes = {
  value: PropTypes.string,
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool,
};

Radio.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  value: PropTypes.string,
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool,
};

// Definir un tipo personalizado para RadioOption
const radioOptionType = PropTypes.oneOfType([
  PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string.isRequired,
    checked: PropTypes.bool,
  }),
  PropTypes.element,
]);

RadioContainer.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(radioOptionType).isRequired,
  className: PropTypes.string,
  onChange: PropTypes.func,
  initialValue: PropTypes.string,
  value: PropTypes.string,
  customStyle: PropTypes.object,
  prefix: PropTypes.node,
  suffix: PropTypes.node,
};
